import React, { useState, useEffect, useCallback } from 'react';
import { Alert, Button, Timeline, Tag, Empty, Spin, message, Tooltip, Modal } from 'antd';
import {
  UserSwitchOutlined, CheckCircleOutlined, ClockCircleOutlined,
  HistoryOutlined, InboxOutlined, UserAddOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import AffecterDossierModal from './AffecterDossierModal';
import { affectationService, corbeilleService } from '../../../services/affectationService';
import {
  getUser, can, HAB, CHARTE_COLORS as C, formatDateHeure,
} from '../../../variables/constants';

import { avecLibelleTraduit } from '../../../i18n/libelles';

/**
 * Affectation du dossier : qui le traite, qui le lui a confié, et depuis quand.
 *
 * Répond au TDR - « encadrer et tracer une trajectoire pour chaque utilisateur et
 * retrouver facilement leurs traitements ». Chaque réaffectation crée une ligne :
 * l'historique n'est jamais écrasé.
 */

const STATUTS = avecLibelleTraduit({
  EN_ATTENTE_PRISE_EN_CHARGE: { color: 'gold', icon: <ClockCircleOutlined /> },
  PRISE_EN_CHARGE: { color: 'green', icon: <CheckCircleOutlined /> },
  REAFFECTE: { color: 'default', icon: <UserSwitchOutlined /> },
}, 'dossierSinistre:panneauAffectation.statuts');

const delaiLisible = (heures, t) => {
  if (heures == null) return null;
  if (heures < 1) return t('panneauAffectation.delaiMoinsUneHeure');
  if (heures < 24) return t('panneauAffectation.delaiHeures', { n: heures });
  const j = Math.round(heures / 24);
  return t('panneauAffectation.delaiJours', { count: j });
};

export default function PanneauAffectation({ sinistre, onChange }) {
  const { t } = useTranslation('dossierSinistre');
  const [historique, setHistorique] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [modalOuverte, setModalOuverte] = useState(false);
  const [prise, setPrise] = useState(false);

  const moi = getUser();
  const sinistreId = sinistre?.sinistreTrackingId;

  const charger = useCallback(async () => {
    if (!sinistreId) return;
    setChargement(true);
    try {
      const r = await affectationService.historique(sinistreId);
      setHistorique(r.data?.data ?? []);
    } catch {
      setHistorique([]);
    } finally {
      setChargement(false);
    }
  }, [sinistreId]);

  useEffect(() => { charger(); }, [charger]);

  // L'affectation en cours = la première non réaffectée (l'API trie du plus récent au plus ancien).
  const courante = historique.find((a) => a.statut !== 'REAFFECTE');
  const jeSuisLeDestinataire = courante && moi
    && courante.affecteATrackingId === moi.trackingId;
  const aAccuserReception = jeSuisLeDestinataire
    && courante.statut === 'EN_ATTENTE_PRISE_EN_CHARGE';

  /* Qui peut faire quoi :
     - un dossier en attente d'affectation se PREND soi-même (SINISTRE_AUTO_AFFECTATION) ;
     - l'agent qui traite le dossier peut le RÉAFFECTER à un collègue, sans être chef ;
     - un responsable (SINISTRE_ASSIGN) affecte et réaffecte les dossiers des autres. */
  const peutDistribuer = can(HAB.SINISTRE_ASSIGN);
  const peutPrendre = !courante && sinistre?.statut === 'SOUMISE' && can(HAB.SINISTRE_AUTO_AFFECTATION);
  const peutReaffecter = !!courante && (jeSuisLeDestinataire || peutDistribuer);

  const prendreLeDossier = () => {
    const mobile = sinistre?.canal === 'MOBILE';
    Modal.confirm({
      title: t('panneauAffectation.prendreTitre', { numero: sinistre?.numeroSinistreLocal || '' }),
      icon: <UserAddOutlined style={{ color: C.greenMid }} />,
      content: mobile
        ? t('panneauAffectation.prendreMobile') : t('panneauAffectation.prendreWeb'),
      okText: t('panneauAffectation.sAffecter'),
      cancelText: t('panneauAffectation.annuler'),
      okButtonProps: { style: { background: C.greenMid, borderColor: C.greenMid } },
      onOk: async () => {
        try {
          const r = await corbeilleService.mAffecter(sinistreId);
          message.success(r.data?.message || t('panneauAffectation.prisEnCharge'));
        } catch (e) {
          // « Untel a pris ce dossier avant vous » : cas normal d'une file partagée.
          message.info(e?.response?.data?.message || t('panneauAffectation.prendreImpossible'));
        }
        await charger();
        onChange?.();
      },
    });
  };

  const prendreEnCharge = async () => {
    setPrise(true);
    try {
      const r = await affectationService.prendreEnCharge(sinistreId);
      message.success(r.data?.message || t('panneauAffectation.prisEnCharge'));
      await charger();
      onChange?.();
    } catch (e) {
      message.error(e?.response?.data?.message || t('panneauAffectation.priseEnChargeEchec'));
    } finally {
      setPrise(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* - Bandeau : ce dossier m'a été confié, je dois accuser réception - */}
      {aAccuserReception && (
        <Alert
          type="info"
          showIcon
          icon={<InboxOutlined />}
          message={t('panneauAffectation.confieAVous')}
          description={
            <div>
              <div>
                {courante.affecteParNomPrenom !== '-' ? t('panneauAffectation.affecteParLe', { nom: courante.affecteParNomPrenom, date: formatDateHeure(courante.dateAffectation) })
                  : t('panneauAffectation.affecteLe', { date: formatDateHeure(courante.dateAffectation) })}
              </div>
              {courante.consigne && (
                <div style={{
                  marginTop: 8, padding: '8px 10px', background: '#fff',
                  border: `1px solid ${C.border}`, borderRadius: 6,
                  whiteSpace: 'pre-line', fontSize: 13,
                }}>
                  <strong>{t('panneauAffectation.consigne')}</strong> {courante.consigne}
                </div>
              )}
            </div>
          }
          action={
            <Button type="primary" loading={prise} onClick={prendreEnCharge}
              style={{ background: C.greenMid, borderColor: C.greenMid }}>
              {t('panneauAffectation.prendreEnCharge')}
            </Button>
          }
        />
      )}

      {/* - État courant + action - */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 12, padding: '12px 14px', background: C.bg,
        border: `1px solid ${C.border}`, borderRadius: 8,
      }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 2 }}>{t('panneauAffectation.traitePar')}</div>
          {courante ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 600, color: C.text }}>{courante.affecteANomPrenom}</span>
              <Tag color={STATUTS[courante.statut]?.color} icon={STATUTS[courante.statut]?.icon}
                style={{ margin: 0 }}>
                {STATUTS[courante.statut]?.label}
              </Tag>
              <span style={{ fontSize: 12, color: C.faint }}>
                {t('panneauAffectation.depuisLe', { date: formatDateHeure(courante.dateAffectation) })}
              </span>
            </div>
          ) : (
            <span style={{ color: C.amber, fontWeight: 500 }}>{t('panneauAffectation.personne')}</span>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          {peutPrendre && (
            <Button type="primary" icon={<UserAddOutlined />} onClick={prendreLeDossier}
              style={{ background: C.greenMid, borderColor: C.greenMid }}>
              {t('panneauAffectation.sAffecter')}
            </Button>
          )}
          {(peutReaffecter || (!courante && peutDistribuer)) && (
            <Button
              icon={<UserSwitchOutlined />}
              onClick={() => setModalOuverte(true)}
              type={courante || peutPrendre ? 'default' : 'primary'}
              style={courante || peutPrendre ? undefined : { background: C.greenMid, borderColor: C.greenMid }}
            >
              {courante ? t('panneauAffectation.reaffecter') : t('panneauAffectation.affecter')}
            </Button>
          )}
        </div>
      </div>

      {/* - Trajectoire du dossier - */}
      <div>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6,
          fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 12,
        }}>
          <HistoryOutlined /> {t('panneauAffectation.trajectoire')}
        </div>

        <Spin spinning={chargement}>
          {historique.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={<span style={{ color: C.muted }}>{t('panneauAffectation.jamaisAffecte')}</span>}
            />
          ) : (
            <Timeline
              items={historique.map((a) => ({
                color: a.statut === 'PRISE_EN_CHARGE' ? 'green'
                  : a.statut === 'EN_ATTENTE_PRISE_EN_CHARGE' ? 'gold' : 'gray',
                children: (
                  <div>
                    <div style={{ fontSize: 13 }}>
                      <strong>{a.affecteANomPrenom}</strong>
                      {a.affecteParNomPrenom && a.affecteParNomPrenom !== '-' && (
                        <span style={{ color: C.muted }}> {t('panneauAffectation.confiePar', { nom: a.affecteParNomPrenom })}</span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: C.faint, marginTop: 2 }}>
                      {formatDateHeure(a.dateAffectation)}
                      {a.datePriseEnCharge && (
                        <Tooltip title={t('panneauAffectation.prisEnChargeLe', { date: formatDateHeure(a.datePriseEnCharge) })}>
                          <span style={{ color: C.greenMid, marginLeft: 8 }}>
                            {t('panneauAffectation.prisEnChargeDelai', { delai: delaiLisible(a.delaiPriseEnChargeHeures, t) })}
                          </span>
                        </Tooltip>
                      )}
                      {a.statut === 'REAFFECTE' && (
                        <span style={{ marginLeft: 8 }}>{t('panneauAffectation.reaffecteDepuis')}</span>
                      )}
                    </div>
                    {a.consigne && (
                      <div style={{
                        marginTop: 6, padding: '6px 9px', background: C.bg,
                        border: `1px solid ${C.border}`, borderRadius: 6,
                        fontSize: 12, color: C.muted, whiteSpace: 'pre-line',
                      }}>
                        {a.consigne}
                      </div>
                    )}
                  </div>
                ),
              }))}
            />
          )}
        </Spin>
      </div>

      <AffecterDossierModal
        open={modalOuverte}
        onClose={() => setModalOuverte(false)}
        sinistre={{
          sinistreTrackingId: sinistreId,
          numeroSinistreLocal: sinistre?.numeroSinistreLocal,
          redacteurNomPrenom: courante?.affecteANomPrenom,
        }}
        parTitulaire={!!jeSuisLeDestinataire}
        exclureTrackingId={courante?.affecteATrackingId ?? null}
        onAffecte={async () => { await charger(); onChange?.(); }}
      />
    </div>
  );
}
